<template>
  <div class="admin-homepage">
    <h1>Admin Homepage</h1>
    <section class="chart-section">
      <h2>Yearly Revenue Chart</h2>
      <!-- Button to display chart -->
      <button @click="showChart = true" class="load-chart-button">
        Load Revenue Chart
      </button>
      <!-- Conditionally render revenue-bar-chart based on showChart value -->
      <revenue-bar-chart v-if="showChart" :selected-year="selectedYear" />
    </section>

    <!-- Monthly Report Section -->
    <section class="monthly-report">
      <h2>Generate CoreEvent Monthly Report</h2>
      <div class="date-picker">
        <label for="year">Year:</label>
        <select id="year" v-model="selectedYear">
          <option v-for="year in availableYears" :key="year" :value="year">
            {{ year }}
          </option>
        </select>

        <label for="month">Month:</label>
        <select id="month" v-model="selectedMonth">
          <option
            v-for="(monthName, index) in months"
            :key="index + 1"
            :value="index + 1"
            :disabled="isMonthDisabled(index + 1)"
          >
            {{ monthName }}
          </option>
        </select>
      </div>

      <router-link :to="monthlyReportLink" class="report-link"> View Monthly Report </router-link>
    </section>
    <hr />

    <!-- Organization Report Section -->
    <section class="organization-report">
      <h2>Organization Report</h2>
      <div class="organization-picker">
        <label for="organization">Organization:</label>
        <v-select
          id="organization"
          v-model="selectedOrganization"
          :options="organizations"
          label="legalName"
          :filterable="true"
          placeholder="Type to search organizations"
        />
      </div>

      <div class="date-range-picker">
        <label for="fromDate">From:</label>
        <input id="fromDate" v-model="fromDate" type="date" :max="currentDate" />

        <label for="toDate">To:</label>
        <input id="toDate" v-model="toDate" type="date" :max="currentDate" />
      </div>

      <router-link
        v-if="selectedOrganization && fromDate && toDate"
        :to="organizationReportLink"
        class="report-link"
      >
        View Organization Report
      </router-link>
    </section>
  </div>
</template>

<script>
import gql from 'graphql-tag'
import vSelect from 'vue-select'
import RevenueBarChart from './RevenueBarChart.vue'
import 'vue-select/dist/vue-select.css'

export default {
  name: 'admin-home-page',
  components: {
    'v-select': vSelect,
    'revenue-bar-chart': RevenueBarChart,
  },
  data() {
    const currentYear = new Date().getFullYear()
    const currentMonth = new Date().getMonth() + 1
    const currentDate = new Date().toISOString().substr(0, 10)

    return {
      currentMonth,
      currentDate,
      selectedYear: currentYear,
      selectedMonth: currentMonth,
      availableYears: this.getAvailableYears(),
      months: [
        'January', 'February', 'March', 'April', 'May', 'June',
        'July', 'August', 'September', 'October', 'November', 'December'
      ],

      showChart: false,

      organizations: [],
      selectedOrganization: null,
      fromDate: '',
      toDate: '',
    }
  },
  computed: {
    monthlyReportLink() {
      return `/admin-pages/monthly-report?year=${this.selectedYear}&month=${this.selectedMonth}`
    },
    organizationReportLink() {
      if (this.selectedOrganization && this.fullFromDate && this.fullToDate) {
        const from = encodeURIComponent(this.fullFromDate.toISOString())
        const to = encodeURIComponent(this.fullToDate.toISOString())
        return `/admin-pages/${this.selectedOrganization.slug}?from=${from}&to=${to}`
      }
      return ''
    },
    fullFromDate() {
      if (this.fromDate) {
        const date = new Date(this.fromDate)
        date.setHours(0, 0, 0, 0)
        return date
      }
      return null
    },
    fullToDate() {
      if (this.toDate) {
        const date = new Date(this.toDate)
        date.setHours(23, 59, 59, 999)
        return date
      }
      return null
    },
  },
  methods: {
    getAvailableYears() {
      const currentYear = new Date().getFullYear()
      const startYear = 2020
      const years = []
      for (let year = startYear; year <= currentYear; year++) {
        years.push(year)
      }
      return years
    },
    isMonthDisabled(month) {
      const currentYear = new Date().getFullYear()
      if (this.selectedYear === currentYear) {
        return month > this.currentMonth
      }
      return false
    },
  },
  apollo: {
    organizations: {
      query: gql`
        query GetAllOrganizations {
          getAllOrganizations
        }
      `,
      result({ data }) {
        if (data && data.getAllOrganizations) {
          this.organizations = data.getAllOrganizations
        }
      },
    },
  },
}
</script>

<style scoped>
.chart-section {
  margin-top: 20px;
  max-width: 800px;
}
.load-chart-button {
  padding: 10px 20px;
  background-color: #0277bd;
  color: #fff;
  border: none;
  border-radius: 4px;
  cursor: pointer;
  margin-bottom: 20px;
}
.load-chart-button:hover {
  background-color: #01579b;
}
hr {
  margin: 20px 0;
}
.organization-picker {
  width: 400px;
}
.admin-homepage {
  padding: 20px;
}

h1,
h2 {
  color: #333;
}

.date-picker {
  margin-bottom: 20px;
}

.date-picker label {
  margin-right: 5px;
}

.date-picker select {
  margin-right: 15px;
}

.report-link {
  display: inline-block;
  padding: 10px 15px;
  background-color: #0277bd;
  color: #fff;
  text-decoration: none;
  border-radius: 4px;
}

.report-link:hover {
  background-color: #01579b;
}

.organization-picker,
.date-range-picker {
  margin-bottom: 20px;
}

.organization-picker label,
.date-range-picker label {
  margin-right: 5px;
}

.organization-picker select,
.date-range-picker input {
  margin-right: 15px;
}

/* Styles for vue-select */
.vue-select {
  width: 300px;
  margin-right: 15px;
}

.vue-select .dropdown-toggle {
  padding: 5px 10px;
}

.vue-select .vs__dropdown-menu {
  max-height: 200px;
  overflow-y: auto;
}
</style>
